import { ControlledCheckbox, ControlledTextField } from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import { AsyncActionButton } from '@src/components'
import { useUpdateTerritory } from '@src/data/api/territories-api/territories-api'
import { Territory, TerritoryRow } from '@src/data/types/Territory'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  title: string
  handleClose: () => void
  row: TerritoryRow | null
}

const EditTerritoryDialog = ({ open, handleClose, row }: Props) => {
  const { t } = useTranslation()
  const [updateTerritory, isUpdatingTerritory, resetUpdateTerritory] = useUpdateTerritory()

  const { handleSubmit, reset, control } = useForm<Territory>({
    mode: 'onBlur',
  })

  const onSubmit = async (data: Territory) => {
    await updateTerritory(data).then((value) => {
      resetUpdateTerritory()
      if (value) handleClose()
    })
  }

  useEffect(() => {
    reset(row ? { ...row, id: String(row.id) } : { id: '', name: '', isManuallyAssigned: false })
  }, [open, reset, row])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      transitionDuration={0}
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        <IconButton
          aria-label="close"
          onClick={() => {
            handleClose()
          }}
          style={{
            position: 'absolute',
            right: 15,
            top: 15,
          }}
        >
          <CloseIcon />
        </IconButton>
        {t('common.editTerritory')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <ControlledTextField name="name" control={control} id="name" label={t('common.name')} required fullWidth />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <Tooltip title={t('browseTerritories.isManuallyAssignedHelp')} arrow placement="top">
              <span>
                <ControlledCheckbox
                  name="isManuallyAssigned"
                  control={control}
                  label={t('browseTerritories.isManuallyAssigned')}
                />
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid width="100%">
          <Stack direction="row" spacing={2}>
            <AsyncActionButton
              fullWidth
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              isPending={isUpdatingTerritory}
            >
              {t('common.save')}
            </AsyncActionButton>
            <AsyncActionButton fullWidth variant="outlined" onClick={() => handleClose()}>
              {t('common.cancel')}
            </AsyncActionButton>
          </Stack>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default memo(EditTerritoryDialog)
